import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { AppContext } from "../../../store/AppScopeProvider";
import { errorMessage, successMessage } from "../../../utils";
import moment from "moment";
import qs from "qs";
import {
  createPointPromotionApi,
  updatePointPromotionApi,
  getPointPromotionDetailsApi,
  getLoyaltyCardsListApi,
} from "../api";

export const useAddOrEditPointPromotion = ({ id } = {}) => {
  const { appState } = useContext(AppContext);
  const { globalData } = appState;
  const navigate = useNavigate();

  const [state, setState] = useImmer({
    isButtonBusy: false,
    isLoading: false,
    data: {
      name: "",
      type: "sales_multiplier",
      card_type: null,
      multiplier_value: null,
      bonus_value: null,
      start_date: "",
      end_date: "",
      limit_by_days_time: false,
      days: null,
      start_time: null,
      end_time: null,
      status: "draft",
      segment_ids: null,
      loyalty_card_group_ids: null,
    },
  });

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setState((draft) => {
      draft.data[name] = type === "checkbox" ? checked : value;
      if (name === "card_type" && value !== "customer") {
        draft.data.segment_ids = [];
      }
      if (name === "card_type" && value !== "sponsorship") {
        draft.data.loyalty_card_group_ids = [];
      }
    });
  };

  const handleLoyaltyCardsList = async ({ search, page }) => {
    let params = {};
    const pagination = {
      pageIndex: page,
      pageSize: 25,
    };

    if (search) {
      params = { ...params, search };
    }

    const query = {
      params,
      paramsSerializer: (queryParams) => qs.stringify(queryParams),
    };

    try {
      const res = await getLoyaltyCardsListApi(query, pagination);
      if (res?.success) {
        const list = res?.data?.loyalty_card_groups || [];
        const options = list.map((item) => ({
          value: item?.id,
          label: item?.club_name,
        }));

        return {
          options,
          hasMore: res?.data?.pagination?.hasMorePages ?? false,
          additional: {
            page: page + 1,
          },
        };
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Unable to fetch loyalty card groups",
      );
    }

    return {
      options: [],
      hasMore: false,
      additional: {
        page,
      },
    };
  };

  useEffect(() => {
    if (id) {
      getPointPromotionDetail();
    }
  }, [id]);

  const getPointPromotionDetail = async () => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });

      const res = await getPointPromotionDetailsApi(id);

      if (res?.success) {
        setState((draft) => {
          draft.data = {
            name: res.data.name || "",
            type: res.data.type || "sales_multiplier",
            card_type: res.data.card_type || "sponsorship",
            multiplier_value: res.data.multiplier_value || 2,
            bonus_value: res.data.bonus_value || null,
            start_date: res.data.start_date || "",
            end_date: res.data.end_date || "",
            limit_by_days_time: res.data.limit_by_days_time || false,
            days: res.data.days || null,
            start_time: res.data.start_time || null,
            end_time: res.data.end_time || null,
            status: res.data.status || "draft",
            segment_ids:
              (res.data.segments || res.data.segment_ids || []).map((item) => ({
                value: item?.value ?? item?.id ?? item,
                label:
                  item?.label ??
                  item?.name ??
                  item?.title ??
                  `${item?.id ?? item}`,
              })) || [],
            loyalty_card_group_ids:
              (
                res.data.loyalty_cards ||
                res.data.loyalty_card_groups ||
                res.data.loyalty_card_group_ids ||
                []
              ).map((item) => ({
                value: item?.value ?? item?.id ?? item,
                label:
                  item?.label ??
                  item?.card_number ??
                  item?.customer_name ??
                  item?.name ??
                  `${item?.id ?? item}`,
              })) || [],
          };
          draft.isLoading = false;
        });
      } else {
        setState((draft) => {
          draft.isLoading = false;
        });
        errorMessage(res?.message || "Unable to fetch point promotion details");
      }
    } catch (err) {
      setState((draft) => {
        draft.isLoading = false;
      });
      errorMessage(
        err?.response?.data?.message ||
          "Unable to fetch point promotion details",
      );
    }
  };

  const onSavePointPromotion = async (status = "draft") => {
    try {
      setState((draft) => {
        draft.isButtonBusy = true;
      });

      const formatTimeForApi = (timeValue) => {
        if (!timeValue) return null;
        const parsedTime = moment(
          timeValue,
          ["HH:mm:ss", "HH:mm", "hh:mm A"],
          true,
        );
        return parsedTime.isValid() ? parsedTime.format("HH:mm") : null;
      };

      const payload = {
        ...state.data,
        status,
        segment_ids: (state.data.segment_ids || []).map(
          (item) => item?.value ?? item?.id ?? item,
        ),
        loyalty_card_group_ids: (state.data.loyalty_card_group_ids || []).map(
          (item) => item?.value ?? item?.id ?? item,
        ),
        start_time: formatTimeForApi(state.data.start_time),
        end_time: formatTimeForApi(state.data.end_time),
        multiplier_value:
          state.data.type === "sales_multiplier"
            ? Number(state.data.multiplier_value || 0)
            : null,
        bonus_value:
          state.data.type === "sales_bonus"
            ? Number(state.data.bonus_value || 0)
            : null,
      };

      let res;
      if (id) {
        res = await updatePointPromotionApi(id, payload);
      } else {
        res = await createPointPromotionApi(payload);
      }

      if (res?.success) {
        successMessage(
          res?.message ||
            (id
              ? "Point promotion updated successfully"
              : "Point promotion saved successfully"),
        );
        navigate("/point-promotions");
      } else {
        errorMessage(
          res?.message ||
            (id
              ? "Unable to update point promotion"
              : "Unable to save point promotion"),
        );
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message ||
          (id
            ? "Unable to update point promotion"
            : "Unable to save point promotion"),
      );
    } finally {
      setState((draft) => {
        draft.isButtonBusy = false;
      });
    }
  };

  return {
    state,
    globalData,
    handleChange,
    handleLoyaltyCardsList,
    onSavePointPromotion,
  };
};
